import React from 'react';
import {
    Table as MuiTable,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableFooter,
    TablePagination,
    Paper,
    Box,
    Typography,
    CircularProgress
} from '@material-ui/core';
import PropTypes from 'prop-types';

const Table = ({
    columns,
    data,
    loading,
    error,
    page,
    rowsPerPage,
    totalCount,
    onPageChange,
    onRowsPerPageChange,
    className,
    emptyStateMessage = 'No data available',
    errorMessage = 'An error occurred while loading data',
    loadingMessage = 'Loading...',
    onRowClick,
    getRowId = (row) => row.id,
    stickyHeader = false,
    size = 'medium',
    showPagination = true,
    rowsPerPageOptions = [10, 25, 50],
    customEmptyState,
    customErrorState,
    customLoadingState,
}) => {
    const handleRowClick = (row) => {
        if (onRowClick) {
            onRowClick(row);
        }
    };

    const renderCell = (row, column) => {
        if (column.render) {
            return column.render(row[column.accessor], row);
        }
        return row[column.accessor];
    };

    const renderEmptyState = () => {
        if (customEmptyState) {
            return customEmptyState;
        }
        return (
            <TableRow>
                <TableCell colSpan={columns.length} align="center">
                    <Typography variant="body2" color="textSecondary">
                        {emptyStateMessage}
                    </Typography>
                </TableCell>
            </TableRow>
        );
    };

    const renderErrorState = () => {
        if (customErrorState) {
            return customErrorState;
        }
        return (
            <TableRow>
                <TableCell colSpan={columns.length} align="center">
                    <Typography variant="body2" color="error">
                        {errorMessage}
                    </Typography>
                </TableCell>
            </TableRow>
        );
    };

    const renderLoadingState = () => {
        if (customLoadingState) {
            return customLoadingState;
        }
        return (
            <TableRow>
                <TableCell colSpan={columns.length} align="center">
                    <Box display="flex" alignItems="center" justifyContent="center" p={2}>
                        <CircularProgress size={24} />
                        <Box ml={2}>
                            <Typography variant="body2" color="textSecondary">
                                {loadingMessage}
                            </Typography>
                        </Box>
                    </Box>
                </TableCell>
            </TableRow>
        );
    };

    return (
        <TableContainer component={Paper} className={className}>
            <MuiTable stickyHeader={stickyHeader} size={size}>
                <TableHead>
                    <TableRow>
                        {columns.map((column) => (
                            <TableCell
                                key={column.accessor}
                                align={column.align || 'left'}
                                style={column.headerStyle}
                            >
                                {column.header}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading && renderLoadingState()}
                    {error && renderErrorState()}
                    {!loading && !error && data.length === 0 && renderEmptyState()}
                    {!loading && !error && data.length > 0 && data.map((row) => (
                        <TableRow
                            key={getRowId(row)}
                            onClick={() => handleRowClick(row)}
                            hover={!!onRowClick}
                            style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                        >
                            {columns.map((column) => (
                                <TableCell
                                    key={`${getRowId(row)}-${column.accessor}`}
                                    align={column.align || 'left'}
                                    style={column.cellStyle}
                                >
                                    {renderCell(row, column)}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
                {showPagination && (
                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={rowsPerPageOptions}
                                count={totalCount}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={onPageChange}
                                onRowsPerPageChange={onRowsPerPageChange}
                                SelectProps={{
                                    inputProps: { 'aria-label': 'rows per page' },
                                    native: true,
                                }}
                            />
                        </TableRow>
                    </TableFooter>
                )}
            </MuiTable>
        </TableContainer>
    );
};

Table.propTypes = {
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            header: PropTypes.string.isRequired,
            accessor: PropTypes.string.isRequired,
            align: PropTypes.oneOf(['left', 'center', 'right']),
            render: PropTypes.func,
            headerStyle: PropTypes.object,
            cellStyle: PropTypes.object,
        })
    ).isRequired,
    data: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    error: PropTypes.bool,
    page: PropTypes.number,
    rowsPerPage: PropTypes.number,
    totalCount: PropTypes.number,
    onPageChange: PropTypes.func,
    onRowsPerPageChange: PropTypes.func,
    className: PropTypes.string,
    emptyStateMessage: PropTypes.string,
    errorMessage: PropTypes.string,
    loadingMessage: PropTypes.string,
    onRowClick: PropTypes.func,
    getRowId: PropTypes.func,
    stickyHeader: PropTypes.bool,
    size: PropTypes.oneOf(['small', 'medium']),
    showPagination: PropTypes.bool,
    rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
    customEmptyState: PropTypes.node,
    customErrorState: PropTypes.node,
    customLoadingState: PropTypes.node,
};

export default Table; 